import styled, { css } from 'styled-components';

const statusColor = (status, theme) => {
  switch (status) {
    case 'erro':
      return theme.colors.red;
    case 'atencao':
      return theme.colors.yellow;
    case 'parada':
      return theme.colors.label;
    case 'normal':
    default:
      return theme.colors.green;
  }
};

const colorFromKeyword = (kw, theme) => {
  if (!kw) return theme.colors.darkerblue;
  switch (kw) {
    case 'green':
      return theme.colors.green;
    case 'orange':
      return theme.colors.orange;
    case 'red':
      return theme.colors.red;
    default:
      return kw;
  }
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px 12px;
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 13px;
  color: ${(props) => props.theme.colors.label};
  font-style: italic;
`;

// Heatstrip — agora bem mais compacta (barra de 12px de altura). Serve
// so como ranking visual de relance; detalhes ficam nos mini-cards.
export const HeatstripWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

export const HeatstripLabel = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const Heatstrip = styled.div`
  display: flex;
  align-items: stretch;
  gap: 3px;
  flex: 1;
  min-width: 0;
  height: 14px;
`;

export const HeatstripCell = styled.button`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  background: transparent;
  border: 1.5px solid
    ${(props) =>
      props.$selected
        ? statusColor(props.$status, props.theme)
        : 'transparent'};
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 150ms, transform 150ms;
  overflow: hidden;

  :hover {
    transform: translateY(-1px);
    border-color: ${(props) => props.theme.colors.lightblue};
  }
`;

export const HeatstripBar = styled.div`
  width: 100%;
  height: 100%;
  background: ${(props) => statusColor(props.$status, props.theme)};
  opacity: ${(props) => 0.3 + props.$ratio * 0.7};
  border-radius: 2px;
  transition: opacity 250ms;
`;

// Sort bar
export const SortBar = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const SortLabel = styled.span`
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};
`;

export const SortChip = styled.button`
  padding: 2px 9px;
  height: 20px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 120ms;
  font-family: inherit;
  border: 1px solid
    ${(props) =>
      props['aria-pressed'] === 'true'
        ? props.theme.colors.lightblue
        : props.theme.colors.disabled};
  background: ${(props) =>
    props['aria-pressed'] === 'true'
      ? props.theme.colors.lightblue
      : 'transparent'};
  color: ${(props) =>
    props['aria-pressed'] === 'true'
      ? props.theme.colors.white
      : props.theme.colors.label};

  :hover {
    border-color: ${(props) => props.theme.colors.lightblue};
    color: ${(props) =>
      props['aria-pressed'] === 'true'
        ? props.theme.colors.white
        : props.theme.colors.lightblue};
  }
`;

// Grid de mini-cards — 2 a 4 colunas dependendo da largura. Cada card e
// uma maquina; mostra 4 metricas em layout 2x2 dentro. Substitui a lista
// vertical que so mostrava 1 linha visivel devido a constraint de altura.
export const MachineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.disabled};
    border-radius: 3px;
  }
`;

export const MachineCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  background: ${(props) =>
    props.$selected
      ? props.theme.title === 'light'
        ? 'rgba(0, 159, 216, 0.08)'
        : 'rgba(59, 191, 241, 0.10)'
      : props.theme.title === 'light'
      ? 'rgba(0, 0, 0, 0.02)'
      : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid
    ${(props) =>
      props.$selected
        ? props.theme.colors.lightblue
        : props.theme.colors.disabled};
  transition: border-color 120ms, background 120ms, transform 120ms;

  :hover {
    border-color: ${(props) => props.theme.colors.lightblue};
    transform: translateY(-1px);
  }

  /* Faixa lateral pequena na cor do status pra dar acento visual rapido */
  ${(props) =>
    props.$selected &&
    css`
      box-shadow: inset 3px 0 0 ${statusColor(props.$status, props.theme)};
      padding-left: 13px;
    `}
`;

export const MachineCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => statusColor(props.$status, props.theme)};
  flex-shrink: 0;
`;

export const MachineName = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: ${(props) => props.theme.colors.darkerblue};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const MachineCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 8px;
`;

export const MachineCardMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

export const MachineCardMetricLabel = styled.span`
  font-size: 8.5px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.label};
`;

export const MachineCardMetricValue = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) =>
    props.negative
      ? props.theme.colors.red
      : colorFromKeyword(props.color, props.theme)};
`;
